import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowUpRight, FlaskConical, GitCompare, History, Save, Play, Plus, Pencil, Trash2 } from 'lucide-react'
import {
  dryRunPrompt,
  llmCalls as llmCallsApi,
  promotePromptVersion,
  promptSlots as slotsApi,
  promptVersions as versionsApi,
  sandboxRuns as sandboxRunsApi,
  type DiffWordOp,
  type PromptSlot,
  type PromptVersion,
} from '../api'
import { useArtifactPicker } from '../components/useArtifactPicker'
import { useAuth } from '../auth'

type Tab = 'edit' | 'diff' | 'dryrun' | 'recent'

export default function PromptsPage() {
  const slots = useQuery({ queryKey: ['promptSlots'], queryFn: () => slotsApi.list() })
  const [slotId, setSlotId] = useState<number | null>(null)
  const [versionId, setVersionId] = useState<number | null>(null)

  const versions = useQuery({
    queryKey: ['promptVersions', slotId],
    queryFn: () => (slotId ? versionsApi.bySlot(slotId) : Promise.resolve([])),
    enabled: !!slotId,
  })

  return (
    <div className="grid h-full grid-cols-[240px_240px_1fr] gap-px bg-[color:var(--me-border)]">
      <SlotsList
        slots={slots.data ?? []}
        selectedId={slotId}
        onSelect={(id) => {
          setSlotId(id)
          setVersionId(null)
        }}
        onSlotsChanged={() => slots.refetch()}
      />
      <VersionsList versions={versions.data ?? []} selectedId={versionId} onSelect={setVersionId} />
      <VersionDetail
        slot={slots.data?.find((s) => s.id === slotId) ?? null}
        versions={versions.data ?? []}
        selectedId={versionId}
        onChanged={() => {
          versions.refetch()
          slots.refetch()
        }}
      />
    </div>
  )
}

function SlotsList({ slots, selectedId, onSelect, onSlotsChanged }: {
  slots: PromptSlot[]; selectedId: number | null; onSelect: (id: number) => void
  onSlotsChanged: () => void
}) {
  const [filter, setFilter] = useState<'production' | 'sandbox'>('production')
  const [showNew, setShowNew] = useState(false)
  const visible = slots.filter((s) => filter === 'sandbox' ? s.is_sandbox : !s.is_sandbox)
  return (
    <aside className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-3">
      <div className="mb-1.5 inline-flex overflow-hidden rounded-sm border me-border">
        {(['production', 'sandbox'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-selected={filter === f}
            className="me-btn !rounded-none !text-[10px] !px-2 !py-1"
            style={filter === f ? { background: 'var(--me-selected)', color: 'var(--me-text)', fontWeight: 600 } : {}}
          >
            {f}
          </button>
        ))}
      </div>
      <div className="me-section-h">
        {filter === 'sandbox' ? 'Sandbox slots' : 'Production slots'} · {visible.length}
      </div>
      {filter === 'sandbox' && (
        <>
          <button
            onClick={() => setShowNew((v) => !v)}
            className="me-btn me-btn-outline mb-2 w-full !text-[11px] !py-1"
            title="Sandbox slots are for prompt iteration only — no DAG impact, no production state changes."
          >
            <Plus size={11} className="inline" /> New sandbox slot
          </button>
          {showNew && (
            <NewSandboxSlotForm
              onClose={() => setShowNew(false)}
              onCreated={(slot) => {
                setShowNew(false)
                onSlotsChanged()
                onSelect(slot.id)
              }}
            />
          )}
        </>
      )}
      {visible.map((s) => (
        <SlotRow
          key={s.id}
          slot={s}
          selected={selectedId === s.id}
          onSelect={() => onSelect(s.id)}
          onSlotsChanged={onSlotsChanged}
        />
      ))}
      {visible.length === 0 && (
        <div className="px-2 pt-1 text-[12px] me-text-mute italic">
          {filter === 'sandbox' ? 'No sandbox slots yet. Create one to test prompts.' : 'No production slots.'}
        </div>
      )}
    </aside>
  )
}

function SlotRow({
  slot, selected, onSelect, onSlotsChanged,
}: {
  slot: PromptSlot
  selected: boolean
  onSelect: () => void
  onSlotsChanged: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(slot.name)
  const [description, setDescription] = useState(slot.description || '')
  const update = useMutation({
    mutationFn: () => slotsApi.update(slot.id, { name: name.trim(), description: description.trim() }),
    onSuccess: () => { setEditing(false); onSlotsChanged() },
  })
  const remove = useMutation({
    mutationFn: () => slotsApi.remove(slot.id),
    onSuccess: () => onSlotsChanged(),
  })

  if (editing) {
    return (
      <div className="me-card mb-1 p-2 space-y-1.5">
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="me-input w-full !text-[11.5px] !py-1"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="description (optional)"
          className="me-input w-full !text-[11.5px] !py-1"
        />
        <div className="flex justify-end gap-1">
          <button onClick={() => { setEditing(false); setName(slot.name); setDescription(slot.description || '') }} className="me-btn me-btn-outline !text-[11px] !py-1">Cancel</button>
          <button
            onClick={() => update.mutate()}
            disabled={!name.trim() || update.isPending}
            className="me-btn me-btn-primary !text-[11px] !py-1"
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
        {update.error && (
          <div className="text-[10.5px] text-[color:var(--me-stale)]">{(update.error as Error).message}</div>
        )}
      </div>
    )
  }

  return (
    <div className="group relative">
      <button
        onClick={onSelect}
        data-selected={selected ? 'true' : undefined}
        className="me-row mb-0.5 w-full"
      >
        <div className="flex items-center gap-2">
          <span className="me-chip">L{slot.level}</span>
          <span className="font-medium me-text truncate">{slot.name}</span>
          {slot.is_sandbox && (
            <span className="me-chip me-chip-stale" title="Sandbox slot — no DAG impact">sandbox</span>
          )}
        </div>
        <div className="me-mono mt-0.5 text-[11px] me-text-mute">
          {slot.version_count} version{slot.version_count === 1 ? '' : 's'}
          {slot.production_version_id && (
            <>
              {' · '}
              <span className="text-[color:var(--me-fresh)]">production set</span>
            </>
          )}
        </div>
      </button>
      {/* Edit / delete only on sandbox slots — production slots are
          managed via seed_real_dag, never directly. Visible on hover. */}
      {slot.is_sandbox && (
        <div className="absolute top-1 right-1 hidden group-hover:flex items-center gap-0.5">
          <button
            onClick={(e) => { e.stopPropagation(); setEditing(true) }}
            className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5"
            title="Edit name / description"
          >
            <Pencil size={9} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (confirm(`Delete sandbox slot "${slot.name}"? This removes the slot, all its versions, and all sandbox runs against it.`)) {
                remove.mutate()
              }
            }}
            disabled={remove.isPending}
            className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5 text-[color:var(--me-stale)]"
            title="Delete sandbox slot"
          >
            <Trash2 size={9} />
          </button>
        </div>
      )}
    </div>
  )
}

function NewSandboxSlotForm({
  onClose, onCreated,
}: { onClose: () => void; onCreated: (s: PromptSlot) => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () => slotsApi.create({
      name: name.trim(), description: description.trim(),
      level: 1, output_schema: {}, is_sandbox: true,
    } as Partial<PromptSlot>),
    onSuccess: (slot) => onCreated(slot),
    onError: (e) => setError((e as Error).message),
  })
  return (
    <div className="mb-2 me-card p-2 space-y-1.5">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="slot name (e.g. test_subject_clf)"
        className="me-input w-full !text-[11.5px] !py-1"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="optional description"
        rows={2}
        className="me-input w-full !text-[11.5px] !py-1"
      />
      <div className="flex justify-end gap-1">
        <button onClick={onClose} className="me-btn me-btn-outline !text-[11px] !py-1">Cancel</button>
        <button
          onClick={() => create.mutate()}
          disabled={!name.trim() || create.isPending}
          className="me-btn me-btn-primary !text-[11px] !py-1"
        >
          {create.isPending ? 'Creating…' : 'Create sandbox slot'}
        </button>
      </div>
      {error && <div className="text-[10.5px] text-[color:var(--me-stale)]">{error}</div>}
    </div>
  )
}

function VersionsList({ versions, selectedId, onSelect }: { versions: PromptVersion[]; selectedId: number | null; onSelect: (id: number) => void }) {
  return (
    <aside className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-3">
      <div className="me-section-h">Versions</div>
      {versions.map((v) => (
        <button
          key={v.id}
          onClick={() => onSelect(v.id)}
          data-selected={selectedId === v.id ? 'true' : undefined}
          className="me-row mb-0.5"
        >
          <div className="flex items-center justify-between">
            <span className="me-mono font-medium me-text">v{v.version}</span>
            {v.labels.includes('production') && <span className="me-chip me-chip-fresh">production</span>}
          </div>
          <div className="me-mono mt-0.5 truncate text-[11px] me-text-mute">
            {v.commit_message || new Date(v.created_at).toLocaleString()}
          </div>
        </button>
      ))}
      {versions.length === 0 && <div className="px-2 pt-1 text-[12px] me-text-mute">Select a slot.</div>}
    </aside>
  )
}

function VersionDetail({
  slot, versions, selectedId, onChanged,
}: { slot: PromptSlot | null; versions: PromptVersion[]; selectedId: number | null; onChanged: () => void }) {
  const [tab, setTab] = useState<Tab>('edit')
  if (!slot || !selectedId) {
    return (
      <section className="me-scroll bg-[color:var(--me-surface)] p-5">
        <div className="text-[12px] me-text-mute">Select a slot, then a version.</div>
      </section>
    )
  }
  const current = versions.find((v) => v.id === selectedId)
  const prior = versions.find((v) => v.version === (current?.version ?? 0) - 1)
  if (!current) return null
  const isProduction = current.labels.includes('production')

  return (
    <section className="me-scroll flex flex-col overflow-hidden bg-[color:var(--me-surface)]">
      <div className="border-b me-border px-5 pt-4 pb-2">
        <div className="me-section-h">{slot.name}</div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="me-mono text-[16px] font-medium me-text">v{current.version}</h3>
            {isProduction && <span className="me-chip me-chip-fresh">production</span>}
            {current.labels.filter((l) => l !== 'production').map((l) => <span key={l} className="me-chip">{l}</span>)}
          </div>
          {!isProduction && (
            <PromoteBtn versionId={current.id} slotIsSandbox={slot.is_sandbox} onChanged={onChanged} />
          )}
        </div>
        <div className="me-mono mt-1 text-[11px] me-text-mute">
          {current.model} · {new Date(current.created_at).toLocaleString()}
          {current.created_by_username && <> · by {current.created_by_username}</>}
        </div>
        {current.commit_message && (
          <div className="mt-1 text-[12px] me-text-dim italic">{current.commit_message}</div>
        )}
        {current.var_warnings && current.var_warnings.length > 0 && (
          <div className="mt-2 rounded-sm border border-[color:var(--me-stale)] bg-[color:var(--me-staleBg)] px-2 py-1 text-[11px] text-[color:var(--me-stale)]">
            ⚠ Unknown variables in this prompt: {current.var_warnings.map((w) => `{{${w}}}`).join(', ')} — they will be passed through as literal text at runtime.
          </div>
        )}

        <div className="mt-3 flex gap-1 -mb-2">
          {([
            ['edit',   'Edit',     <Save size={12} key="i" />],
            ['diff',   'Compare',  <GitCompare size={12} key="i" />],
            ['dryrun', 'Test run', <FlaskConical size={12} key="i" />],
            ['recent', 'Runs',     <History size={12} key="i" />],
          ] as const).map(([key, label, icon]) => (
            <button
              key={key}
              onClick={() => setTab(key as Tab)}
              data-selected={tab === key ? 'true' : undefined}
              className="me-tab"
            >{icon}{label}</button>
          ))}
        </div>
      </div>

      <div className="me-scroll flex-1 overflow-auto p-5">
        {tab === 'edit'   && <EditorTab slot={slot} version={current} onSaved={onChanged} />}
        {tab === 'diff'   && <DiffTab versions={versions} current={current} prior={prior} />}
        {tab === 'dryrun' && <DryRunTab version={current} />}
        {tab === 'recent' && <RecentRunsTab version={current} />}
      </div>
    </section>
  )
}

function PromoteBtn({ versionId, slotIsSandbox, onChanged }: { versionId: number; slotIsSandbox: boolean; onChanged: () => void }) {
  const auth = useAuth()
  const isSuperuser = auth.status === 'authenticated' && auth.user.is_superuser
  const qc = useQueryClient()
  const promote = useMutation({
    mutationFn: () => promotePromptVersion(versionId, 'production'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['results'] })
      qc.invalidateQueries({ queryKey: ['results-all'] })
      onChanged()
    },
  })
  // Promotion is platform-admin-only — backend rejects studio_editor.
  // Hide the button rather than render a doomed click. Sandbox slots
  // technically have a "production" label too (their sole label), but
  // promotion there is meaningless because nothing executes against them
  // — still gated for consistency.
  if (!isSuperuser) return null
  void slotIsSandbox
  return (
    <button
      onClick={() => promote.mutate()}
      disabled={promote.isPending}
      className="me-btn me-btn-primary"
    >
      <ArrowUpRight size={12} />
      {promote.isPending ? 'Promoting…' : 'Promote → production'}
    </button>
  )
}

// ---------- Edit tab ----------

function EditorTab({ slot, version, onSaved }: { slot: PromptSlot; version: PromptVersion; onSaved: () => void }) {
  const auth = useAuth()
  const isSuperuser = auth.status === 'authenticated' && auth.user.is_superuser
  const canSave = isSuperuser || slot.is_sandbox
  const [content, setContent] = useState(version.content)
  const [commit, setCommit] = useState('')
  const taRef = useRef<HTMLTextAreaElement>(null)
  const qc = useQueryClient()

  // Reset when version changes
  useEffect(() => { setContent(version.content); setCommit('') }, [version.id])

  const vars = useQuery({
    queryKey: ['slot-vars', slot.id],
    queryFn: () => slotsApi.vars(slot.id),
  })

  const usedVars = useMemo(() => {
    const re = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g
    const found = new Set<string>()
    let m: RegExpExecArray | null
    while ((m = re.exec(content)) !== null) found.add(m[1])
    return found
  }, [content])

  const missing = useMemo(() => {
    if (!vars.data) return new Set<string>()
    const avail = new Set(Object.keys(vars.data.vars))
    return new Set([...usedVars].filter((v) => !avail.has(v)))
  }, [usedVars, vars.data])

  function insertVar(name: string) {
    const ta = taRef.current
    if (!ta) {
      setContent(content + `{{${name}}}`)
      return
    }
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const insert = `{{${name}}}`
    const next = content.slice(0, start) + insert + content.slice(end)
    setContent(next)
    requestAnimationFrame(() => {
      ta.focus()
      ta.selectionStart = ta.selectionEnd = start + insert.length
    })
  }

  const save = useMutation({
    mutationFn: () => versionsApi.create({
      slot: slot.id, content, model: version.model || 'fake-model',
      labels: [], commit_message: commit || `(edited from v${version.version})`,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promptVersions', slot.id] })
      onSaved()
    },
  })

  const isDirty = content !== version.content

  return (
    <div className="grid grid-cols-[1fr_220px] gap-4">
      <div>
        <div className="me-section-h">Content</div>
        <textarea
          ref={taRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="me-mono me-input w-full min-h-[280px] resize-y leading-relaxed"
          spellCheck={false}
        />
        <div className="mt-3 me-section-h">Commit message</div>
        <input
          value={commit}
          onChange={(e) => setCommit(e.target.value)}
          placeholder={`(edited from v${version.version})`}
          className="me-input"
        />
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={() => save.mutate()}
            disabled={!isDirty || save.isPending || !canSave}
            className="me-btn me-btn-primary"
            title={!canSave
              ? 'Production prompts are platform-admin-only. Use a sandbox slot to iterate.'
              : ''}
          >
            <Save size={12} />
            {save.isPending ? 'Saving…' : `Save as v${version.version + 1}`}
          </button>
          {!canSave && (
            <span className="me-chip me-chip-stale">
              read-only — production slot
            </span>
          )}
          {missing.size > 0 && (
            <span className="me-chip me-chip-stale">
              unresolved: {[...missing].join(', ')}
            </span>
          )}
          {isDirty && missing.size === 0 && canSave && (
            <span className="me-mono text-[11px] me-text-mute">unsaved changes</span>
          )}
        </div>
      </div>

      <aside>
        <div className="me-section-h">Available variables</div>
        <div className="space-y-1">
          {vars.data && Object.entries(vars.data.vars).map(([name, desc]) => (
            <button
              key={name}
              onClick={() => insertVar(name)}
              className="me-card w-full p-2 text-left hover:border-[color:var(--me-borderStrong)]"
              title={desc}
            >
              <div className="me-mono text-[11.5px] me-text">
                {`{{${name}}}`}{usedVars.has(name) && <span className="me-chip me-chip-fresh ml-2 text-[10px]">in use</span>}
              </div>
              <div className="text-[10.5px] me-text-mute mt-0.5 leading-snug">{desc}</div>
            </button>
          ))}
        </div>
      </aside>
    </div>
  )
}

// ---------- Diff tab ----------

function DiffTab({ versions, current, prior }: { versions: PromptVersion[]; current: PromptVersion; prior: PromptVersion | undefined }) {
  const [compareIds, setCompareIds] = useState<number[]>(prior ? [prior.id, current.id] : [current.id])
  useEffect(() => {
    if (prior && !compareIds.includes(current.id)) setCompareIds([prior.id, current.id])
  }, [current.id, prior?.id])

  const diff = useQuery({
    queryKey: ['diff', compareIds],
    queryFn: () => versionsApi.diff(compareIds),
    enabled: compareIds.length >= 2,
  })

  function toggle(id: number) {
    setCompareIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])
  }

  return (
    <div>
      <div className="me-section-h">Pick versions to compare ({compareIds.length} selected)</div>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {versions.map((v) => (
          <button
            key={v.id}
            onClick={() => toggle(v.id)}
            data-selected={compareIds.includes(v.id) ? 'true' : undefined}
            className="me-tab"
          >
            <span className="me-mono">v{v.version}</span>
            {v.labels.includes('production') && <span className="me-chip me-chip-fresh">prod</span>}
          </button>
        ))}
      </div>

      {compareIds.length < 2 && (
        <div className="text-[12px] me-text-mute">Pick at least 2 versions.</div>
      )}

      {diff.data && (
        <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${diff.data.versions.length}, 1fr)` }}>
          {diff.data.versions.map((v, i) => {
            const baseEntry = diff.data!.diffs[0]  // base column reuses first entry's lines_a
            const entry = i > 0 ? diff.data!.diffs[i - 1] : null
            return (
              <div key={v.id} className="me-card p-3">
                <div className="mb-2 flex items-center gap-2">
                  <span className="me-mono font-medium">v{v.version}</span>
                  {v.labels.includes('production') && <span className="me-chip me-chip-fresh">production</span>}
                  {i === 0 && <span className="me-chip">base</span>}
                </div>
                <DiffColumn
                  lines={i === 0 ? (baseEntry?.lines_a ?? []) : entry!.lines_b}
                  side={i === 0 ? 'a' : 'b'}
                  opcodes={i === 0 ? (baseEntry?.opcodes ?? null) : entry!.opcodes}
                  wordOps={i === 0 ? (baseEntry?.word_ops ?? []) : entry!.word_ops}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function DiffColumn({
  lines,
  opcodes,
  wordOps,
  side,
}: {
  lines: string[]
  opcodes: [string, number, number, number, number][] | null
  wordOps: DiffWordOp[]
  side: 'a' | 'b'
}) {
  if (!opcodes) {
    return (
      <pre className="me-mono whitespace-pre-wrap text-[11.5px] leading-relaxed">
        {lines.map((l, i) => (<div key={i}>{l || ' '}</div>))}
      </pre>
    )
  }
  // Map line index -> opcode tag (defaults to 'equal' when an opcode covers no line on this side).
  const tagByLine = new Map<number, string>()
  for (const [tag, i1, i2, j1, j2] of opcodes) {
    const [start, end] = side === 'a' ? [i1, i2] : [j1, j2]
    for (let k = start; k < end; k++) tagByLine.set(k, tag)
  }
  // Map line index -> its word-level diff entry, when one was emitted server-side.
  const wordOpByLine = new Map<number, DiffWordOp>()
  for (const wo of wordOps) {
    wordOpByLine.set(side === 'a' ? wo.line_a : wo.line_b, wo)
  }
  return (
    <pre className="me-mono whitespace-pre-wrap text-[11.5px] leading-relaxed">
      {lines.map((l, i) => {
        const tag = tagByLine.get(i) || 'equal'
        const wordOp = wordOpByLine.get(i)
        // For 'replace' lines that have a word-level entry, skip the block bg
        // so the per-word highlights aren't drowned out.
        const blockCls =
          tag === 'equal' ? '' :
          tag === 'delete' ? 'bg-[color:var(--me-urgentBg)]' :
          tag === 'insert' ? 'bg-[color:var(--me-freshBg)]' :
          tag === 'replace' && !wordOp ? 'bg-[color:var(--me-staleBg)] text-[color:var(--me-stale)]' :
          ''
        return (
          <div key={i} className={blockCls}>
            {wordOp && tag === 'replace'
              ? <WordRow wordOp={wordOp} side={side} />
              : (l || ' ')}
          </div>
        )
      })}
    </pre>
  )
}

function WordRow({ wordOp, side }: { wordOp: DiffWordOp; side: 'a' | 'b' }) {
  const words = side === 'a' ? wordOp.a_words : wordOp.b_words
  const tagByWord = new Map<number, string>()
  for (const [tag, i1, i2, j1, j2] of wordOp.opcodes) {
    const [start, end] = side === 'a' ? [i1, i2] : [j1, j2]
    for (let k = start; k < end; k++) tagByWord.set(k, tag)
  }
  return (
    <>
      {words.map((w, i) => {
        const t = tagByWord.get(i) || 'equal'
        const cls =
          t === 'equal' ? '' :
          t === 'delete' ? 'bg-[color:var(--me-urgentBg)] text-[color:var(--me-urgent)] rounded-sm px-0.5' :
          t === 'insert' ? 'bg-[color:var(--me-freshBg)] text-[color:var(--me-fresh)] rounded-sm px-0.5' :
          t === 'replace' ? 'bg-[color:var(--me-staleBg)] text-[color:var(--me-stale)] rounded-sm px-0.5' : ''
        return (
          <span key={i} className={cls}>
            {w}{i < words.length - 1 ? ' ' : ''}
          </span>
        )
      })}
    </>
  )
}

// ---------- Dry-run tab ----------

function DryRunTab({ version }: { version: PromptVersion }) {
  const [artifactId, setArtifactId] = useState<number | null>(null)
  const { filter, setFilter, artifacts: filtered, isFetching } = useArtifactPicker({ limit: 50 })

  const run = useMutation({
    mutationFn: () => dryRunPrompt({ prompt_version_id: version.id, artifact_id: artifactId! }),
  })

  return (
    <div>
      <div className="me-section-h">Pick an artifact to dry-run against</div>
      <div className="mb-3 grid grid-cols-[1fr_auto] gap-2">
        <input
          placeholder="search artifacts by subject / sender / body…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="me-input"
        />
        <button
          onClick={() => artifactId && run.mutate()}
          disabled={!artifactId || run.isPending}
          className="me-btn me-btn-primary"
        >
          <Play size={12} />
          {run.isPending ? 'Running…' : 'Run'}
        </button>
      </div>
      <div className="me-card max-h-56 overflow-auto p-1">
        {isFetching && filtered.length === 0 && (
          <div className="px-3 py-2 text-[11px] me-text-mute italic">Searching…</div>
        )}
        {!isFetching && filtered.length === 0 && (
          <div className="px-3 py-2 text-[11px] me-text-mute italic">
            {filter.trim() ? 'No matching artifacts.' : 'No artifacts.'}
          </div>
        )}
        {filtered.map((a) => (
          <button
            key={a.id}
            onClick={() => setArtifactId(a.id)}
            data-selected={artifactId === a.id ? 'true' : undefined}
            className="me-row"
          >
            <div className="flex items-center gap-2">
              <span className="me-chip uppercase">{a.artifact_type}</span>
              <span className="truncate font-medium me-text">{a.subject || '(no subject)'}</span>
            </div>
            <div className="me-mono mt-0.5 text-[10px] me-text-mute">{a.from_email}</div>
          </button>
        ))}
      </div>

      {run.data && (
        <div className="mt-5">
          {/* Output first — it's almost always shorter than the rendered
           *  input and it's what the user is actually checking. Input is
           *  below for reference. */}
          <div className="me-section-h">Output</div>
          <pre className="me-mono whitespace-pre-wrap rounded-md bg-[color:var(--me-panel)] p-3 text-[11.5px] leading-relaxed me-text">
            {run.data.output_text}
          </pre>
          <div className="mt-2 me-mono text-[11px] me-text-mute">
            {run.data.model} · in={run.data.input_tokens} out={run.data.output_tokens} · {run.data.latency_ms}ms · NOT persisted
          </div>
          <div className="mt-3 me-section-h">Rendered input</div>
          <pre className="me-mono whitespace-pre-wrap rounded-md bg-[color:var(--me-panel)] p-3 text-[11.5px] leading-relaxed me-text">
            {run.data.rendered_input}
          </pre>
        </div>
      )}
      {run.error && (
        <div className="mt-3 text-[12px] text-[color:var(--me-urgent)]">{(run.error as Error).message}</div>
      )}
    </div>
  )
}

// ---------- Recent runs tab ----------

type RunSourceFilter = 'all' | 'dag' | 'dry_run' | 'test_set' | 'pipeline_test' | 'sandbox_slot'

function RecentRunsTab({ version }: { version: PromptVersion }) {
  // Two sources surface here:
  //   - LlmCallLog ("DAG"): real production runs (only populated for
  //     production slots; sandbox slots never auto-fire)
  //   - SandboxRun: every iteration run — dry_run, test_set, pipeline_test,
  //     sandbox_slot. Source field tags which surface produced it.
  // Filter dropdown lets the user narrow to one source ("show me ONLY
  // the runs I just kicked off from this page", say). Paginated client-
  // side over the merged list (50 per page, capped 500 total fetch).
  const [sourceFilter, setSourceFilter] = useState<RunSourceFilter>('all')
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 25
  const [expandedId, setExpandedId] = useState<string | null>(null)
  useEffect(() => { setPage(0) }, [sourceFilter])

  const calls = useQuery({
    queryKey: ['llmCalls', 'version', version.id],
    queryFn: () => llmCallsApi.list({ slot: version.slot }),
    enabled: sourceFilter === 'all' || sourceFilter === 'dag',
  })
  const sandbox = useQuery({
    queryKey: ['sandboxRuns', 'version', version.id, sourceFilter],
    queryFn: () => sandboxRunsApi.list({
      prompt_version: version.id,
      ...(sourceFilter !== 'all' && sourceFilter !== 'dag' ? { source: sourceFilter } : {}),
      limit: 500,
    } as Record<string, string | number>),
    enabled: sourceFilter !== 'dag',
  })

  type Row = {
    kind: 'real' | 'sandbox'
    id: string
    ts: string
    sourceTag: string
    statusText: string
    statusOk: boolean
    headline: string
    sub: string
    fullInput: string
    fullOutput: string
    error?: string
  }
  const realRows: Row[] = (sourceFilter === 'all' || sourceFilter === 'dag'
    ? (calls.data || []).filter((c) => c.prompt_version === version.id)
    : []
  ).map((c) => ({
    kind: 'real', id: `r-${c.id}`, ts: c.created_at,
    sourceTag: 'DAG',
    statusText: c.status, statusOk: c.status === 'success',
    headline: c.artifact_subject || (c.artifact ? `artifact #${c.artifact}` : '(no artifact)'),
    sub: `${c.artifact_composite_id || ''} · ${c.step_name || '—'} · ${c.model} · in=${c.input_tokens} out=${c.output_tokens} · ${c.latency_ms}ms`,
    fullInput: c.input || '(empty / redacted)',
    fullOutput: c.output || '(empty / redacted)',
    error: c.error_message || undefined,
  }))
  const sandboxRows: Row[] = (sourceFilter === 'dag' ? [] : (sandbox.data || [])).map((s) => ({
    kind: 'sandbox', id: `s-${s.id}`, ts: s.created_at,
    sourceTag: s.source,
    statusText: s.status, statusOk: s.status === 'success',
    headline: s.artifact_subject || (s.artifact ? `artifact #${s.artifact}` : '(no artifact)'),
    sub: `${s.artifact_composite_id || ''} · in=${s.input_tokens} out=${s.output_tokens} · ${s.latency_ms}ms`
      + (s.created_by_username ? ` · by ${s.created_by_username}` : '')
      + ' · not in production state',
    fullInput: s.rendered_input || '(empty)',
    fullOutput: s.output_text || '(empty)',
    error: s.error_message || undefined,
  }))
  const allRows = [...realRows, ...sandboxRows].sort((a, b) => b.ts.localeCompare(a.ts))
  const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE))
  const pageRows = allRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as RunSourceFilter)}
          className="me-input w-auto text-[12px] py-1.5"
        >
          <option value="all">All sources</option>
          <option value="dag">DAG (production)</option>
          <option value="dry_run">Dry-run (this page)</option>
          <option value="test_set">Test set</option>
          <option value="pipeline_test">Pipeline test</option>
          <option value="sandbox_slot">Sandbox slot</option>
        </select>
        <span className="me-mono text-[11px] me-text-mute">
          {allRows.length.toLocaleString()} runs · page {page + 1}/{totalPages} · v{version.version}
        </span>
        <span className="ml-auto" />
        <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
          className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">‹ prev</button>
        <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
          className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">next ›</button>
      </div>
      {pageRows.length === 0 && (
        <div className="text-[12px] me-text-mute italic">No runs recorded for this filter yet.</div>
      )}
      <ul className="space-y-1">
        {pageRows.map((r) => {
          const expanded = expandedId === r.id
          return (
            <li key={r.id} className="me-card p-2.5">
              <button
                onClick={() => setExpandedId(expanded ? null : r.id)}
                className="w-full text-left"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="me-mono text-[11.5px] truncate flex-1 min-w-0">
                    <span className={`me-chip mr-1 ${r.kind === 'real' ? 'me-chip-fresh' : 'me-chip-stale'}`}>
                      {r.sourceTag}
                    </span>
                    {r.headline}
                  </span>
                  <span className={r.statusOk ? 'me-chip me-chip-fresh' : 'me-chip me-chip-urgent'}>{r.statusText}</span>
                </div>
                <div className="me-mono mt-0.5 text-[10px] me-text-mute truncate">
                  {new Date(r.ts).toLocaleString()} · {r.sub}
                </div>
                {!expanded && r.fullOutput && (
                  <div className="me-mono mt-1 truncate text-[10.5px] me-text-mute">
                    {r.fullOutput.slice(0, 200)}
                  </div>
                )}
              </button>
              {expanded && (
                <div className="mt-2 space-y-2">
                  {/* Output first (usually short, what the user is checking),
                   *  then Input (often long context). Errors at the bottom
                   *  if any. */}
                  <div>
                    <div className="me-section-h !mb-0.5">Output</div>
                    <pre className="me-mono whitespace-pre-wrap rounded-md bg-[color:var(--me-panel)] p-2 text-[11px] leading-relaxed max-h-72 overflow-auto">{r.fullOutput}</pre>
                  </div>
                  <div>
                    <div className="me-section-h !mb-0.5">Input</div>
                    <pre className="me-mono whitespace-pre-wrap rounded-md bg-[color:var(--me-panel)] p-2 text-[11px] leading-relaxed max-h-72 overflow-auto">{r.fullInput}</pre>
                  </div>
                  {r.error && (
                    <div>
                      <div className="me-section-h !mb-0.5">Error</div>
                      <pre className="me-mono whitespace-pre-wrap text-[11px] text-[color:var(--me-urgent)]">{r.error}</pre>
                    </div>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
